import { BackendLimits } from '@/components/constants/backend-limits';

/**
 * Analyzes an upload batch to determine if chunking is needed.
 * @param {Array} files - Array of file objects with meta properties
 * @returns {Object} Analysis results with needsChunking flag
 */
export function analyzeUploadBatch(files) {
  const stats = {
    totalFiles: files.length,
    uniqueFolders: new Set(),
    rootNodes: new Map(),
    needsChunking: false,
  };

  // Group by contextNodeId and count unique folders
  for (const file of files) {
    const rootId = file.meta?.contextNodeId || 'root';
    const path = file.meta?.webkitRelativePath;

    if (path?.includes('/')) {
      const folderPath = path.substring(0, path.lastIndexOf('/'));
      stats.uniqueFolders.add(`${rootId}:${folderPath}`);
    }

    if (!stats.rootNodes.has(rootId)) {
      stats.rootNodes.set(rootId, []);
    }
    stats.rootNodes.get(rootId).push(file);
  }

  stats.needsChunking =
    stats.totalFiles > BackendLimits.MAX_PODS_PER_NODE ||
    stats.uniqueFolders.size > BackendLimits.MAX_NODES_PER_ROOT;

  return stats;
}

/**
 * Extracts root folders from file paths.
 * @param {Array} files - Array of file objects
 * @returns {Array} Array of unique root folder names
 */
function extractRootFolders(files) {
  const rootFolders = new Set();

  for (const file of files) {
    const path = file.meta?.webkitRelativePath;
    if (path?.includes('/')) {
      const firstFolder = path.split('/')[0];
      rootFolders.add(firstFolder);
    }
  }

  return Array.from(rootFolders);
}

/**
 * Checks if files need chunking based on limits.
 * @param {Array} files - Array of file objects
 * @param {Object} limits - Backend limits object
 * @returns {boolean} True if chunking is needed
 */
function needsChunking(files, limits) {
  const uniqueFolders = new Set();
  for (const file of files) {
    const path = file.meta?.webkitRelativePath;
    if (path?.includes('/')) {
      const folderPath = path.substring(0, path.lastIndexOf('/'));
      uniqueFolders.add(folderPath);
    }
  }

  return (
    files.length > limits.MAX_PODS_PER_NODE ||
    uniqueFolders.size > limits.MAX_NODES_PER_ROOT
  );
}

/**
 * Extracts unique folder structure from files.
 * @param {Array} files - Array of file objects
 * @returns {Set} Set of unique folder paths
 */
export function extractFolderStructure(files) {
  const folders = new Set();

  for (const file of files) {
    const path = file.meta?.webkitRelativePath;
    if (path?.includes('/')) {
      const parts = path.split('/');
      // Add all intermediate folder paths
      for (let i = 0; i < parts.length - 1; i++) {
        const folderPath = parts.slice(0, i + 1).join('/');
        folders.add(folderPath);
      }
    }
  }

  return folders;
}

/**
 * Splits files into chunks of specified size.
 * @param {Array} files - Files to split
 * @param {number} chunkSize - Maximum files per chunk
 * @returns {Array} Array of chunk objects
 */
function splitIntoChunks(files, chunkSize) {
  const chunks = [];
  for (let i = 0; i < files.length; i += chunkSize) {
    chunks.push({
      files: files.slice(i, i + chunkSize),
      chunkType: 'files_only',
    });
  }
  return chunks;
}

/**
 * Chunks files within a single root folder by first-level subfolders.
 * @param {Array} files - Files to chunk
 * @param {Object} limits - Backend limits
 * @returns {Array} Array of chunk objects
 */
export function chunkSingleRootFolder(files, limits) {
  const chunks = [];
  const rootFolders = extractRootFolders(files);

  // Strategy: Split by first-level folders
  for (const rootFolder of rootFolders) {
    const filesInFolder = files.filter((f) =>
      f.meta?.webkitRelativePath?.startsWith(`${rootFolder}/`)
    );

    if (filesInFolder.length === 0) continue;

    // Chunk 1: Folders + files (up to limit)
    const maxFilesInFirstChunk = Math.min(
      filesInFolder.length,
      limits.MAX_PODS_PER_NODE
    );

    chunks.push({
      files: filesInFolder.slice(0, maxFilesInFirstChunk),
      chunkType: 'folders_and_files',
      rootFolder,
    });

    // Chunk 2+: Remaining files only
    let offset = maxFilesInFirstChunk;
    while (offset < filesInFolder.length) {
      const batchSize = Math.min(
        limits.MAX_PODS_PER_NODE,
        filesInFolder.length - offset
      );

      chunks.push({
        files: filesInFolder.slice(offset, offset + batchSize),
        chunkType: 'files_only',
        rootFolder,
      });

      offset += batchSize;
    }
  }

  // Handle root-level files (no folders)
  const rootLevelFiles = files.filter(
    (f) =>
      !f.meta?.webkitRelativePath ||
      !f.meta.webkitRelativePath.includes('/')
  );

  if (rootLevelFiles.length > 0) {
    chunks.push(...splitIntoChunks(rootLevelFiles, limits.MAX_PODS_PER_NODE));
  }

  return chunks;
}

/**
 * Chunks files by root folder first, then by subfolders if needed.
 * Primary chunking strategy for large uploads.
 * @param {Array} files - Files to chunk
 * @param {Object} limits - Backend limits object
 * @returns {Array} Array of chunk objects
 */
export function chunkFilesByRootFolder(files, limits) {
  const rootNodesMap = new Map();

  // Group by contextNodeId
  for (const file of files) {
    const rootId = file.meta?.contextNodeId || 'root';
    if (!rootNodesMap.has(rootId)) {
      rootNodesMap.set(rootId, []);
    }
    rootNodesMap.get(rootId).push(file);
  }

  // If multiple root nodes, each is a separate chunk
  if (rootNodesMap.size > 1) {
    const chunks = [];
    for (const [, rootFiles] of rootNodesMap) {
      // Recursively chunk within root if needed
      if (needsChunking(rootFiles, limits)) {
        chunks.push(...chunkSingleRootFolder(rootFiles, limits));
      } else {
        chunks.push({ files: rootFiles, chunkType: 'complete' });
      }
    }
    return chunks;
  }

  // Single root node - chunk by folders
  return chunkSingleRootFolder(files, limits);
}
